import React, { useCallback, Fragment, useState } from 'react';
import { useIntl, defineMessages } from 'react-intl-next';
// We have deprecated emotion. Please use compiled instead
// eslint-disable-next-line no-restricted-imports, @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import styled from '@emotion/styled';

import CustomThemeButton from '@atlaskit/button/custom-theme-button';
import ChevronDownIcon from '@atlaskit/icon/glyph/chevron-down';
import WorldIcon from '@atlaskit/icon/glyph/world';
import { N0, N900 } from '@atlaskit/theme/colors';
import { token } from '@atlaskit/tokens';
import AkTooltip, { TooltipPrimitive } from '@atlaskit/tooltip';
import { Flex, xcss } from '@atlaskit/primitives';

import { fg } from '@confluence/feature-gating';
import { borderRadius } from '@confluence/typography-placeholder';
import {
	ShortcutListener,
	ShortcutVisualizer,
	getShortcutString,
	EDITOR2_SHORTCUTS,
} from '@confluence/shortcuts';
import type { FlagsStateContainer } from '@confluence/flags';
import { usePageSpaceKey } from '@confluence/page-context';
import { isCompanyHubSpaceKey } from '@confluence/route-manager/entry-points/companyHubUtils';
import { CompanyHubPublishUpsell } from '@confluence/change-edition/entry-points/CompanyHubPublishUpsell';
import { useIsCompanyHubEnabled } from '@confluence/company-hub-utils/entry-points/useIsCompanyHubEnabled';
import {
	EDITOR_UPDATE_BUTTON_CLICK,
	EDITOR_UPDATE_SHORTCUT_KEY_PRESS,
	setViewTransitionSource,
} from '@confluence/browser-metrics';
import { PublishHubDialogLoadable } from '@confluence/company-hub/entry-points/PublishHubDialogLoadable';
import { useIsTryBeforeYouBuyState } from '@confluence/company-hub-utils/entry-points/useIsTryBeforeYouBuyState';

import { preloadPublishModal } from './preloadPublishModal';
import { displayPublishErrorFlag } from './helpers';
import { PublishErrorFlagKeys, SHORTCUT_KEYS } from './constants';
import { usePublishModalActions } from './usePublishModalActions';
import { i18nShared } from './i18nShared';

const i18n = defineMessages({
	updateButtonTooltip: {
		id: 'publish-modal.update-button-tooltip',
		description:
			'Tooltip clarifying that the update button allows the user to publish without altering settings with the publish modal',
		defaultMessage: 'Update with current settings',
	},
	updateButtonTooltipCompanyHub: {
		id: 'publish-modal.update-button-tooltip-for-system-space',
		description:
			'Tooltip clarifying that the update button allows the user to publish without altering settings with the publish modal when Company Hub Landing Page is visible',
		defaultMessage: 'Update landing page',
	},
	publishButtonTooltipCompanyHub: {
		id: 'publish-modal.publish-button-tooltip-for-system-space',
		description:
			'Tooltip clarifying that the publish button allows the user to publish without altering settings with the publish modal when Company Hub Landing Page is not visible',
		defaultMessage: 'Publish landing page',
	},
	publishButtonDisabledTooltipCompanyHub: {
		id: 'publish-modal.publish-button-disabled-tooltip-company-hub',
		description:
			'Tooltip to clarify that the publish button is disabled for non-admins of Company Hub and that the changes are saved',
		defaultMessage: 'Only admins can publish at this time - your changes have been saved.',
	},
	publishButtonCompanyHub: {
		id: 'publish-modal.publish-button-for-system-space',
		description:
			'Button for user to publish the page without bringing up the publish modal when Company Hub Landing Page is not visible',
		defaultMessage: 'Publish',
	},
});

export type UpdateSplitButtonProps = {
	publishButtonDisabled: boolean | undefined;
	shortcutIsEnabled: boolean;
	openModal: (() => void) | undefined;
	saveDraft: (() => void) | undefined;
	isTitleEmpty: boolean | undefined;
	flags: FlagsStateContainer;
	savingInProgress: boolean;
	shouldShowWorldIcon: boolean;
	contentType?: string;
};

const UpdateSplitContainerStyles = xcss({
	position: 'relative',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/design-system/no-styled-tagged-template-expression -- Ignored via go/DSP-18766
const VerticalSeparator = styled.div`
	width: 1px;
	background-color: ${token('color.background.input', N0)};
`;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/design-system/no-styled-tagged-template-expression -- Ignored via go/DSP-18766
const TooltipCustomizationComponent = styled(TooltipPrimitive)`
	background: ${token('color.background.neutral.bold', N900)};
	color: ${token('color.text.inverse', N0)};
	box-sizing: border-box;
	max-width: 300px;
	padding: ${token('space.025', '2px')} ${token('space.075', '6px')};
	top: 0;
	left: 0;
	border-radius: ${borderRadius}px;
	font-size: 12px;
	line-height: 1.3;
	overflow-wrap: break-word;
	word-wrap: break-word;
`;

const leftButtonTheme = (currentTheme, themeProps, isCompanyHub) => {
	const { buttonStyles, spinnerStyles } = currentTheme(themeProps);
	return {
		buttonStyles: {
			...buttonStyles,
			...(!isCompanyHub && {
				borderTopRightRadius: 0,
				borderBottomRightRadius: 0,
				marginRight: 0,
			}),
		},
		spinnerStyles,
	};
};

const rightButtonTheme = (currentTheme, themeProps) => {
	const { buttonStyles, spinnerStyles } = currentTheme(themeProps);
	return {
		buttonStyles: {
			...buttonStyles,
			borderTopLeftRadius: 0,
			borderBottomLeftRadius: 0,
			marginLeft: 0,
		},
		spinnerStyles,
	};
};

export const UpdateSplitButton = ({
	publishButtonDisabled,
	shortcutIsEnabled,
	openModal,
	saveDraft,
	isTitleEmpty,
	flags,
	savingInProgress,
	shouldShowWorldIcon,
	contentType,
}: UpdateSplitButtonProps) => {
	const [isPublishHubOpen, setIsPublishHubOpen] = useState<boolean>(false);
	const intl = useIntl();

	const [spaceKey] = usePageSpaceKey();

	const { handlePublish } = usePublishModalActions();

	const isCompanyHub = isCompanyHubSpaceKey(spaceKey);
	const { canPublish, isCompanyHubPublished } = useIsCompanyHubEnabled();
	const isCompanyHubPublishDisabled = isCompanyHub && !isCompanyHubPublished && !canPublish;
	publishButtonDisabled ||= isCompanyHubPublishDisabled;

	const isTryBeforeYouBuyState = useIsTryBeforeYouBuyState({
		skip: !isCompanyHub,
	});

	const publishWithAnalytics = useCallback(
		async (source = 'shortcut') => {
			const trigger =
				source !== 'button' ? EDITOR_UPDATE_SHORTCUT_KEY_PRESS : EDITOR_UPDATE_BUTTON_CLICK;

			if (contentType && contentType === 'page') {
				setViewTransitionSource(trigger);
			}

			// This check is necessary bc the publish API does not throw an error for empty title, so we need to handle this case
			if (isTitleEmpty) {
				await displayPublishErrorFlag({
					isTitleEmpty,
					validationMessage: PublishErrorFlagKeys.TITLE_IS_BLANK,
					flags,
					formatMessage: intl.formatMessage,
				});
			} else {
				if (
					isCompanyHub &&
					!isCompanyHubPublished &&
					fg('confluence_frontend_company_hub_ga_updates')
				) {
					setIsPublishHubOpen(true);
				} else {
					void handlePublish({ analyticsSource: 'UpdateSplitButton' });
				}
			}
		},
		[
			isTitleEmpty,
			flags,
			intl.formatMessage,
			handlePublish,
			contentType,
			isCompanyHub,
			isCompanyHubPublished,
		],
	);

	const onPublishButtonClick = async () => {
		if (isTryBeforeYouBuyState) {
			return;
		}
		await publishWithAnalytics('button');
	};

	const buttonTooltipText = () => {
		if (isCompanyHub) {
			if (isCompanyHubPublishDisabled) {
				return intl.formatMessage(i18n.publishButtonDisabledTooltipCompanyHub);
			}
			return isCompanyHubPublished
				? intl.formatMessage(i18n.updateButtonTooltipCompanyHub)
				: intl.formatMessage(i18n.publishButtonTooltipCompanyHub);
		}
		return intl.formatMessage(i18n.updateButtonTooltip);
	};

	const buttonText = () => {
		if (isCompanyHub && !isCompanyHubPublished) {
			return intl.formatMessage(i18n.publishButtonCompanyHub);
		}
		return intl.formatMessage(i18nShared.updateButton);
	};

	return (
		<Flex testId="update-split-container" xcss={UpdateSplitContainerStyles}>
			{shortcutIsEnabled && !publishButtonDisabled && (
				<ShortcutListener
					key={SHORTCUT_KEYS.USB1}
					accelerator={EDITOR2_SHORTCUTS.PUBLISH}
					listener={publishWithAnalytics}
				/>
			)}

			<CompanyHubPublishUpsell isEnabled={isTryBeforeYouBuyState}>
				<AkTooltip
					tag="span"
					component={TooltipCustomizationComponent}
					content={
						<ShortcutVisualizer
							shortcut={getShortcutString(EDITOR2_SHORTCUTS.PUBLISH)}
							contentBefore={buttonTooltipText()}
						/>
					}
				>
					<CustomThemeButton
						appearance="primary"
						onClick={onPublishButtonClick}
						theme={(currentTheme, themeProps) =>
							leftButtonTheme(currentTheme, themeProps, isCompanyHub)
						}
						isDisabled={publishButtonDisabled}
						testId="publish-modal-update-button"
						iconBefore={
							shouldShowWorldIcon ? (
								<WorldIcon label={intl.formatMessage(i18nShared.updatePublicContentLabel)} />
							) : undefined
						}
						isLoading={savingInProgress}
					>
						{buttonText()}
					</CustomThemeButton>
				</AkTooltip>
			</CompanyHubPublishUpsell>
			{openModal && (
				<Fragment>
					<VerticalSeparator />
					<AkTooltip content={intl.formatMessage(i18nShared.chevronButtonTooltip)}>
						<CustomThemeButton
							appearance="primary"
							onClick={openModal}
							iconAfter={
								<ChevronDownIcon
									label={intl.formatMessage(i18nShared.chevronButtonTooltip)}
									size="medium"
								/>
							}
							theme={rightButtonTheme}
							isDisabled={publishButtonDisabled}
							testId="publish-modal-chevron-button"
							onMouseEnter={() => preloadPublishModal(saveDraft)}
							onFocus={() => preloadPublishModal(saveDraft)}
						/>
					</AkTooltip>
				</Fragment>
			)}
			{isPublishHubOpen && (
				<PublishHubDialogLoadable
					onClose={() => setIsPublishHubOpen(false)}
					onPublish={handlePublish}
					savingInProgress={savingInProgress}
				/>
			)}
		</Flex>
	);
};
